/**
 * Bank Transaction API Client (35-kör BE-5/BE-6 — Pénzügy bank-import)
 *
 * A wire-szerződés single-source a `@kgc/api-contract` (ÚJ-wire, a 34-kör invoice-drift
 * ellen: a FE NEM definiál saját zod-ot/típust, hanem a contract-ot importálja).
 *
 * Endpoint-ok (tenant-scoped a BE-n `req.user.tenantId`-ből — a FE NEM küld tenantId-t):
 *   GET    /bank-transactions          → { data: BankTransaction[]; total: number }
 *   GET    /bank-transactions/:id       → { data: BankTransaction }
 *   POST   /bank-transactions          → { data: BankTransaction }   (kézi rögzítés)
 *   POST   /bank-transactions/:id/match → { data: BankTransactionMatch } (Fázis-2, money-write)
 */

import type {
  BankTransaction,
  BankTransactionListQuery,
  BankTransactionMatch,
  CreateBankTransaction,
  MatchBankTransaction,
} from '@kgc/api-contract';

import { api } from './client';

// ============================================
// RESPONSE TYPES (a BE { data, total } / { data } wrapper)
// ============================================

export interface BankTransactionListResponse {
  data: BankTransaction[];
  total: number;
}

export interface BankTransactionResponse {
  data: BankTransaction;
}

// ============================================
// API FUNCTIONS — Fázis 1 (L0/L1: list + create)
// ============================================

/**
 * GET /bank-transactions — banki tétel lista pagination + matchStatus/direction szűrővel.
 * A lista-response `{ data, total }` (nincs `meta`-wrapper, eltér az invoice `{items,total}`-tól).
 */
export async function getBankTransactions(
  query: Partial<BankTransactionListQuery> = {}
): Promise<BankTransactionListResponse> {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.append('page', String(query.page));
  if (query.limit !== undefined) params.append('limit', String(query.limit));
  if (query.matchStatus) params.append('matchStatus', query.matchStatus);
  if (query.direction) params.append('direction', query.direction);

  const qs = params.toString();
  const endpoint = qs ? `/bank-transactions?${qs}` : '/bank-transactions';
  return api.get<BankTransactionListResponse>(endpoint);
}

/**
 * GET /bank-transactions/:id — egyetlen banki tétel.
 */
export async function getBankTransactionById(id: string): Promise<BankTransactionResponse> {
  return api.get<BankTransactionResponse>(`/bank-transactions/${id}`);
}

/**
 * POST /bank-transactions — kézi banki tétel rögzítés.
 * Az `amount` plain number (HUF Ft, ADR-133), `bookingDate` ISO string.
 */
export async function createBankTransaction(
  dto: CreateBankTransaction
): Promise<BankTransactionResponse> {
  return api.post<BankTransactionResponse>('/bank-transactions', dto);
}

// ============================================
// API FUNCTIONS — Fázis 2 (money-write: párosítás)
// ============================================

export interface BankTransactionMatchResponse {
  data: BankTransactionMatch;
}

/**
 * POST /bank-transactions/:id/match — bank-tétel ↔ számla atomi párosítás (Fázis-2, money-write).
 * A BE atomi `$transaction`-ben frissíti az invoice `paidAmount`/`balanceDue`/`status`-t + a
 * bank `matchStatus`-t (R-1 1:N — egy bank-tétel több számlát fedhet). A guard-ok strukturált
 * `error.code`-dal jönnek (`BANK_MATCH_*`, #712) — a hívó a `bankMatchErrorToastKey`-jel
 * code-alapú toast-ot ad.
 */
export async function matchBankTransaction(
  id: string,
  dto: MatchBankTransaction
): Promise<BankTransactionMatchResponse> {
  return api.post<BankTransactionMatchResponse>(`/bank-transactions/${id}/match`, dto);
}
